// Práva v uživatelském rozhraní — rozhodnutí zadavatele 28. 9. 2026, potvrzeno 2. 10. 2026 (docs/01, CLAUDE.md):
//
//   zákaz kvůli ROLI nebo ROZSAHU (server by vrátil 403) → akce se nevykreslí vůbec (tlačítko, odkaz, položka
//                       nabídky, záložka): kdo něco nesmí, o té funkci nemusí vědět;
//   zákaz kvůli STAVU ZÁZNAMU (server by vrátil 409: doklad je vystavený, list je ohlášený, chybí údaj) → neaktivní
//                       tlačítko s důvodem vedle, protože uživatel potřebuje vědět, proč to teď nejde.
//
// Server se nemění — `Pravo` z `muze.*` už oba případy rozlišuje (`konflikt: true` = stav). Tahle funkce a komponenta
// `TlacitkoSDuvodem` (prop `pravo`) to jen převádějí na to, co uživatel vidí. Kde se rozhoduje z `ja.akce` na klientu,
// složí se `Pravo` pomocí `pravoZAkce` a případný stav se přičte `sPrekazkou`.
import type { Pravo } from '@ecoser/shared';

export type { Pravo };

/** Seznam akcí z `GET /ja` (pole, množina, nebo ještě nenačtený). */
export type SeznamAkci = readonly string[] | ReadonlySet<string> | null | undefined;

/** Účet z `GET /ja` s rolemi — hlavní role a všechny role účtu. */
export interface SRolemiJa {
  role?: string | undefined;
  role_efektivni?: string | undefined;
  role_vsechny?: readonly string[] | undefined;
}

/**
 * Všechny role účtu z `GET /ja` (hlavní první). Výsada role platí i pro roli navíc — práva se sčítají (08 § 1 bod 3a;
 * audit W46, C‑07); hlavní role rozhoduje jen o zobrazení (první obrazovka, popisky). Kde to jde, rozhoduj z `ja.akce`
 * nebo `muze.*` ze serveru — role jen tam, kde výsadu žádná akce nepopisuje.
 */
export function roleUctu(ja: SRolemiJa): readonly string[] {
  if (ja.role_vsechny && ja.role_vsechny.length > 0) return ja.role_vsechny;
  const hlavni = ja.role_efektivni || ja.role;
  return hlavni ? [hlavni] : [];
}

/** Má účet roli (hlavní, nebo navíc)? */
export function maRoli(ja: SRolemiJa, role: string): boolean {
  return roleUctu(ja).includes(role);
}

/** Má účet aspoň jednu z rolí (hlavní, nebo navíc)? */
export function maNekterouRoli(ja: SRolemiJa, role: Iterable<string>): boolean {
  const mnozina = new Set(role);
  return roleUctu(ja).some((r) => mnozina.has(r));
}

/** Akce povolená (bez omezení stavem). */
export const POVOLENO: Pravo = { smi: true };

/** Zákaz kvůli roli nebo rozsahu — prvek se nevykresluje (nemá smysl k němu psát důvod). */
export const ZAKAZANO: Pravo = { smi: false };

/** Má účet akci? (`ja.akce` — po sečtení rolí navíc a příznaků účtu už je to jediný zdroj pravdy klienta.) */
export function maAkci(akce: SeznamAkci, klic: string): boolean {
  if (!akce) return false;
  return Array.isArray(akce) ? akce.includes(klic) : (akce as ReadonlySet<string>).has(klic);
}

/**
 * Právo odvozené z `ja.akce`: účet musí mít aspoň jednu z uvedených akcí. Bez ní je to zákaz kvůli roli
 * (`{ smi: false }` bez `konflikt` → prvek se nevykreslí).
 */
export function pravoZAkce(akce: SeznamAkci, ...klice: readonly string[]): Pravo {
  return klice.some((k) => maAkci(akce, k)) ? POVOLENO : ZAKAZANO;
}

/** Účet akci smí a teď jí brání jen stav nebo chybějící údaj → neaktivní s důvodem. */
export function stavovyZakaz(duvod: string, kod?: string): Pravo {
  return { smi: false, duvod, konflikt: true, ...(kod ? { kod } : {}) };
}

/**
 * K právu přičte překážku ve stavu. Kdo akci nesmí kvůli roli, ten ji nevidí, ať je ve stavu cokoli — proto má
 * zákaz kvůli roli přednost a stav se přičte jen povolenému právu.
 */
export function sPrekazkou(pravo: Pravo, duvod: string | null | undefined | false): Pravo {
  if (!pravo.smi || !duvod) return pravo;
  return stavovyZakaz(duvod);
}

/** Je prvek zakázaný kvůli roli nebo rozsahu, tedy se nemá ukázat? (`undefined` = bez omezení práva.) */
export function jeSkryte(pravo: Pravo | undefined): boolean {
  return pravo !== undefined && !pravo.smi && pravo.konflikt !== true;
}

/** Má se prvek ukázat — právo ho dovoluje, nebo je to stav záznamu (neaktivní s důvodem)? */
export function smiVidet(pravo: Pravo | undefined): boolean {
  return !jeSkryte(pravo);
}

/** Smí se akce provést teď? (`undefined` = bez omezení práva.) */
export function smiProvest(pravo: Pravo | undefined): boolean {
  return pravo === undefined || pravo.smi;
}

/**
 * Důvod k zobrazení u akce, kterou účet smí, ale teď nejde (stav záznamu): `pravo.duvod` jen u `konflikt`.
 * Zákaz kvůli roli nebo rozsahu důvod nemá — akce se nevykreslí. Pro komponenty, které berou důvod zvlášť
 * (např. `GaleriePriloh`: `duvodBezPrava`).
 */
export function duvodStavu(pravo: Pravo | undefined): string | undefined {
  return pravo && !pravo.smi && pravo.konflikt ? pravo.duvod : undefined;
}

/**
 * Důvod stavu, který brání aspoň `minimum` akcím najednou (rozhodnutí zadavatele 31. 10. 2026: stejný důvod u více
 * tlačítek nebo řádků se ukáže jednou nad blokem — `BlokSeSpolecnymDuvodem`). Bere jen stavové zákazy (`konflikt`);
 * zákaz kvůli roli se nevykresluje, takže ho není co sdílet. Víc různých důvodů → ten nejčastější (při shodě první).
 */
export function spolecnyDuvodStavu(
  prava: readonly (Pravo | undefined)[],
  minimum = 2,
): string | undefined {
  const pocty = new Map<string, number>();
  for (const pravo of prava) {
    const duvod = duvodStavu(pravo);
    if (duvod) pocty.set(duvod, (pocty.get(duvod) ?? 0) + 1);
  }
  let nej: string | undefined;
  for (const [duvod, pocet] of pocty) {
    if (pocet >= minimum && (nej === undefined || pocet > (pocty.get(nej) ?? 0))) nej = duvod;
  }
  return nej;
}

/**
 * Všechny důvody stavu, které brání aspoň `minimum` akcím (nejčastější první). Pro blok, kde se opakují dva různé
 * důvody (měsíce Celní správy: „XLSX vznikne uzavřením…“ a „PDF vznikne uzavřením…“) — `BlokSeSpolecnymDuvodem`
 * je ukáže nad tabulkou jednou oba a tlačítka v řádcích je neopakují (docs/04 „Kompaktní tabulky“).
 */
export function spolecneDuvodyStavu(prava: readonly (Pravo | undefined)[], minimum = 2): string[] {
  const pocty = new Map<string, number>();
  for (const pravo of prava) {
    const duvod = duvodStavu(pravo);
    if (duvod) pocty.set(duvod, (pocty.get(duvod) ?? 0) + 1);
  }
  return [...pocty]
    .filter(([, pocet]) => pocet >= minimum)
    .sort((a, b) => b[1] - a[1])
    .map(([duvod]) => duvod);
}
